import {
  CTAStandard,
  CTAStandardProps,
  CTAFilled,
  CTACentered,
} from '@zapier/design-system';
import { innerText } from '../../../utils/innerText';
import {
  CTASectionVariant,
  CTASectionVariantType,
} from '../../../global/types';

import { useAvroTracking, useTrackEvent } from '../../../observability';
import {
  CtaGoalType,
  CtaNameType,
  EventName,
} from '../../../observability/tracking/events';
import { RichTextHtml } from '../../RichTextHtml/RichTextHtml';
import { RichTextHTMLString } from '../types';

export interface CTASectionProps
  extends Omit<CTAStandardProps, 'backgroundColor' | 'description'> {
  variant: CTASectionVariantType;
  background: CTAStandardProps['backgroundColor'];
  ctaName: CtaNameType;
  ctaGoal: CtaGoalType;
  description?: RichTextHTMLString;
  openInNewTab: boolean;
}

export function CTASection({
  variant = CTASectionVariant.standard,
  heading,
  subHeading,
  description,
  background,
  ctaText,
  ctaHref,
  ctaName,
  ctaGoal,
  ctaBtnColor,
  children,
  maxWidth,
  openInNewTab,
}: CTASectionProps) {
  const trackEvent = useTrackEvent();
  const { emitCTAClickEvent } = useAvroTracking();

  const pascalCasedVariant =
    variant?.charAt(0)?.toUpperCase() + variant?.slice(1);

  const trackCtaEvent = () => {
    emitCTAClickEvent({
      text: innerText(ctaText),
      href: ctaHref!,
      section: `CTA${pascalCasedVariant}`,
    });

    trackEvent(EventName.CALL_TO_ACTION, {
      path: window.location.pathname,
      from_path: document.referrer,
      cta_name: ctaName,
      cta_raw_text: ctaText,
      cta_goal: ctaGoal,
    });
  };

  const handleCtaClick = () => {
    trackCtaEvent();
  };

  const ctaCommonProps: CTAStandardProps = {
    backgroundColor: background,
    heading,
    subHeading,
    description: description && (
      <RichTextHtml backgroundColor={background} html={description} />
    ),
    ctaHref,
    ctaText,
    ctaBtnColor,
    onCtaClick: handleCtaClick,
    maxWidth,
    ctaTarget: openInNewTab ? '_blank' : '_self',
  };

  const ctaFactory: { [key in CTASectionVariant]: JSX.Element } = {
    [CTASectionVariant.standard]: (
      <CTAStandard {...ctaCommonProps}>{children}</CTAStandard>
    ),
    [CTASectionVariant.filled]: (
      <CTAFilled {...ctaCommonProps}>{children}</CTAFilled>
    ),

    [CTASectionVariant.centered]: <CTACentered {...ctaCommonProps} />,
  };

  return ctaFactory[variant];
}
